import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { toast } from "sonner";
import { SectionCard } from "./shared";
import { bankAccountBlank } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";
import BankAccountForm from "./BankAccountForm";

/**
 * Dedicated edit route: /client/society/accounting/bank-accounts/:id/edit.
 * Leaves the account number blank by default (never fetches it in full just
 * to populate the form) - the backend keeps the existing number unless a new
 * one is explicitly typed.
 *
 * Phase 5 correction: this form never touches the Linked Ledger at all - it
 * is shown read-only. Relinking is a separate, audited administrative
 * action (accounting.approve) deliberately not exposed here, since arbitrary
 * relinking after transactions exist is exactly the risk this correction
 * closes. The backend's normal update endpoint also silently ignores any
 * linkedLedgerId in the request body, as defense in depth.
 */
export default function BankAccountEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [form, setForm] = useState(bankAccountBlank);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: row } = await api.get(`/society/accounting/engine/bank-accounts/${id}`);
        setAccount(row);
        setForm({
          bankName: row.bankName || "", branch: row.branch || "", accountName: row.accountName || "", accountNumber: "",
          ifsc: row.ifsc || "", accountType: row.accountType || "savings", ledgerName: "", linkedLedgerId: "",
          linkedLedgerName: row.linkedLedgerName || "",
          isPrimaryCollectionAccount: !!row.isPrimaryCollectionAccount, reconciliationEnabled: row.reconciliationEnabled !== false,
          active: row.active !== false, isLinkedToBillingSettlement: !!row.isLinkedToBillingSettlement,
          // Prefilled from the linked ledger's own current values (Phase 10
          // Tally-style redesign) so an unrelated edit (e.g. branch name)
          // round-trips the same opening balance instead of accidentally
          // clearing it - see _apply_master_opening_balance on the backend.
          openingBalance: row.openingBalance || 0, openingBalanceSide: row.openingBalanceSide || "debit",
          openingAsOfDate: row.openingAsOfDate || "",
        });
      } catch (err) {
        toast.error(formatErr(err, "Could not load this bank account"));
        navigate(`${ACCOUNTING_BASE}/bank-accounts`, { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const save = async () => {
    if (!form.bankName.trim()) {
      toast.error("Bank name is required");
      return;
    }
    try {
      setBusy(true);
      // ledgerName / linkedLedgerId / linkedLedgerName are display-only in edit mode and are
      // never sent - the backend's normal update endpoint ignores linkedLedgerId anyway, but
      // there is no reason to send fields this form never lets the user change.
      const { ledgerName, linkedLedgerId, linkedLedgerName, ...payload } = form;
      payload.openingBalance = payload.openingBalance === "" ? undefined : Number(payload.openingBalance);
      await api.patch(`/society/accounting/engine/bank-accounts/${id}`, payload);
      toast.success("Bank account updated");
      navigate(`${ACCOUNTING_BASE}/bank-accounts/${id}`);
    } catch (err) {
      toast.error(formatErr(err, "Could not save bank account"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Loading...</div>;
  if (!account) return null;

  return (
    <div className="space-y-5">
      <div>
        <Link to={`${ACCOUNTING_BASE}/bank-accounts/${id}`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
          &larr; Back to account
        </Link>
        <h2 className="mt-1 text-lg font-extrabold text-[#111827]">Edit {account.bankName}</h2>
      </div>

      <SectionCard title="Bank account details" subtitle="Leave the account number blank to keep the existing one unchanged. The linked accounting ledger cannot be changed from here.">
        <BankAccountForm
          form={form}
          setForm={setForm}
          availableLedgers={[]}
          busy={busy}
          onSubmit={save}
          onCancel={() => navigate(`${ACCOUNTING_BASE}/bank-accounts/${id}`)}
          submitLabel="Save changes"
          isEdit
          openingBalanceLocked={!!account.hasPostedTransactions}
        />
      </SectionCard>
    </div>
  );
}
